import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import styled, { keyframes, css } from 'styled-components';
import { useLanguage } from '../Context/LanguageContext';

// Improved animations
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(80, 70, 230, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(80, 70, 230, 0.5);
  }
  100% {
    box-shadow: 0 0 5px rgba(80, 70, 230, 0.3);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Styled components with improved visuals
const MessageContainer = styled.div`
  margin: 0.75rem 0;
  transition: all 0.3s ease;
  animation: ${fadeIn} 0.3s ease-in;
  position: relative;
  max-height: fit-content;
  height: auto;
`;

const AlertBox = styled.div`
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
  height: auto;
  max-height: fit-content;
  min-height: auto;
  background: ${props => props.className === 'error'
    ? '#dc3545'
    : props.className === 'warning'
      ? '#ff9800'
      : props.className === 'training'
        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        : 'linear-gradient(135deg, #12c2e9, #c471ed, #f64f59)'};
  ${props => (props.className === 'training' || props.className === 'info')
    ? css`animation: ${pulseGlow} 2s infinite;`
    : css`animation: none;`}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => (props.className === 'training' || props.className === 'info')
    ? `linear-gradient(
          90deg,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 0.2) 50%,
          rgba(255, 255, 255, 0) 100%
        )`
    : 'none'};
    background-size: 200% 100%;
    ${props => (props.className === 'training' || props.className === 'info')
    ? css`animation: ${shimmer} 3s infinite;`
    : css`animation: none;`}
    z-index: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 0.75rem;
  }
`;

const MessageText = styled.div`
  font-family: var(--font-secondary, sans-serif);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.4;
  color: white;
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const StyledButton = styled(Button)`
  font-family: var(--font-primary, sans-serif);
  font-weight: 600;
  padding: 0.5rem 1.25rem;
  border-radius: var(--radius-sm);
  border: none;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.2s;
  color: white;
  backdrop-filter: blur(5px);
  z-index: 2;
  position: relative;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: var(--radius-sm);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  animation: ${pulseGlow} 2s infinite;
  color: white;
  height: auto;
  max-height: fit-content;
  min-height: auto;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s infinite;
    z-index: 0;
  }
`;

const ErrorText = styled.div`
  color: #ffcccc;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  font-weight: 500;
  font-family: var(--font-secondary, sans-serif);
  animation: ${fadeIn} 0.3s ease-in;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

const SystemStatusMessage = ({
  systemStatus,
  loading,
  queuePosition,
  apiUrl,
  workspaceId,
  onStatusUpdate,
  className
}) => {
  const { t } = useLanguage();
  const [internalError, setInternalError] = useState(null);

  // Ensure systemStatus is a string to avoid errors
  const status = systemStatus != null ? String(systemStatus) : '';

  // Centralized error mapping
  const getErrorMessage = (errorCode) => {
    const mapping = {
      ERROR_CREATION: t('components.systemstatusmessage.errorCreation'),
      ERROR_ADD: t('components.systemstatusmessage.errorAdd'),
      ERROR_DELETE: t('components.systemstatusmessage.errorDelete'),
      ERROR_TRAIN: t('components.systemstatusmessage.errorTrain'),
      ERROR_NO_CONTENT_SCRAPPED_FROM_WEB: t('components.systemstatusmessage.errorNoContentWeb'),
      ERROR_NO_CONTENT_SCRAPPED_FROM_FILE: t('components.systemstatusmessage.errorNoContentFile'),
      ERROR_NO_CONTENT_SCRAPPED_FROM_GOOGLE_DOCS: t('components.systemstatusmessage.errorNoContentGoogleDocs'),
      ERROR_NO_CONTENT_SCRAPPED_FROM_NOTION: t('components.systemstatusmessage.errorNoContentNotion'),
      ERROR_NO_CONTENT_SCRAPPED_FROM_TEXT: t('components.systemstatusmessage.errorNoContentText'),
      ERROR_ASSIGN_CONTENTS_TO_CHATBOTS_AND_APPS: t('components.systemstatusmessage.errorAssignContents'),
      ERROR_UNASSIGN_CONTENTS_FROM_CHATBOTS_AND_APPS: t('components.systemstatusmessage.errorUnassignContents'),
      ERROR_ASSIGN: t('components.systemstatusmessage.errorAssign'),
      ERROR_UNASSIGN: t('components.systemstatusmessage.errorUnassign'),
    };
    return mapping[errorCode] || t('components.systemstatusmessage.errorUnknown');
  };

  // Centralized prepare (and queue) message logic
  const getPrepareMessage = () => {
    if (status.includes('|')) {
      const [prepStatus, count] = status.split('|');
      const mapping = {
        ADD_FILES: `${t('components.systemstatusmessage.preparingFiles')} ${count}`,
        ADD_WEBSITE: `${t('components.systemstatusmessage.preparingWebsite')} ${count}`,
        ADD_LINKS: `${t('components.systemstatusmessage.preparingLinks')} ${count}`,
        ADD_WEBSITE_LINKS: `${t('components.systemstatusmessage.preparingWebsiteLinks')} ${count}`,
        ADD_GOOGLE_DOCS: `${t('components.systemstatusmessage.preparingGoogleDocs')} ${count}`,
      };
      return mapping[prepStatus] || t('components.systemstatusmessage.preparingContent');
    } else if (status.includes('QUEUE')) {
      const [statusStr] = status.split('_QUEUE');
      const mapping = {
        ADD_TEXT: t('components.systemstatusmessage.addingText'),
        ADD_FILES: t('components.systemstatusmessage.addingFiles'),
        ADD_WEBSITE: t('components.systemstatusmessage.addingWebsite'),
        ADD_LINKS: t('components.systemstatusmessage.addingLinks'),
        ADD_WEBSITE_LINKS: t('components.systemstatusmessage.addingWebsiteLinks'),
        ADD_QA: t('components.systemstatusmessage.addingQA'),
        DELETE_CONTENT: t('components.systemstatusmessage.deletingContentQueue'),
        TRAIN: t('components.systemstatusmessage.training'),
        CREATION: t('components.systemstatusmessage.creatingAgent'),
        SCHEDULE_TRAINING: t('components.systemstatusmessage.schedulingTrainingQueue'),
        SCHEDULE_TRAIN: t('components.systemstatusmessage.scheduleTrainQueue'),
        UPDATE_PROMPT: t('components.systemstatusmessage.updatingPrompt'),
        UPDATE_MODEL_AND_TEMPERATURE: t('components.systemstatusmessage.updatingModelAndTemperature'),
        UPDATE_BRAND_SETTINGS: t('components.systemstatusmessage.updatingBrandSettings'),
        ADD_GOOGLE_DOCS: t('components.systemstatusmessage.addingGoogleDocs'),
        ADD_NOTION: t('components.systemstatusmessage.addingNotion'),
        ASSIGN_CONTENTS_TO_CHATBOTS_AND_APPS: t('components.systemstatusmessage.assigningContentsQueue'),
        UNASSIGN_CONTENTS_FROM_CHATBOTS_AND_APPS: t('components.systemstatusmessage.unassigningContentsQueue'),
      };
      const stateString = mapping[statusStr] || t('components.systemstatusmessage.preparingContent');
      if (queuePosition === 'Chatbot is not in the queue') {
        return `${stateString}`;
      }
      return queuePosition === 0 || queuePosition === null
        ? `${stateString} ${t('components.systemstatusmessage.addedToQueue')}`
        : `${stateString} ${t('components.systemstatusmessage.inQueue')} ${queuePosition === 1
          ? t('components.systemstatusmessage.nextInLine')
          : `${t('components.systemstatusmessage.position')} ${queuePosition}`}`;
    } else {
      const mapping = {
        ADD_TEXT: t('components.systemstatusmessage.uploadingText'),
        ADD_FILES: t('components.systemstatusmessage.uploadingFiles'),
        ADD_WEBSITE: t('components.systemstatusmessage.uploadingWebsite'),
        ADD_LINKS: t('components.systemstatusmessage.uploadingLinks'),
        ADD_WEBSITE_LINKS: t('components.systemstatusmessage.uploadingWebsiteLinks'),
        ADD_QA: t('components.systemstatusmessage.uploadingQA'),
        CREATION: t('components.systemstatusmessage.creatingAgentProgress'),
        UPDATE_PROMPT: t('components.systemstatusmessage.updatingPromptProgress'),
        UPDATE_MODEL_AND_TEMPERATURE: t('components.systemstatusmessage.updatingModelAndTemperatureProgress'),
        UPDATE_BRAND_SETTINGS: t('components.systemstatusmessage.updatingBrandSettingsProgress'),
        READY_TO_TRAIN: t('components.systemstatusmessage.readyToTrain'),
        SCHEDULE_TRAINING: t('components.systemstatusmessage.schedulingTrainingProgress'),
        SCHEDULE_TRAIN: t('components.systemstatusmessage.scheduleTrainProgress'),
        ADD_GOOGLE_DOCS: t('components.systemstatusmessage.uploadingGoogleDocs'),
        ADD_NOTION: t('components.systemstatusmessage.uploadingNotion'),
        ASSIGN_CONTENTS_TO_CHATBOTS_AND_APPS: t('components.systemstatusmessage.assigningContentsProgress'),
        UNASSIGN_CONTENTS_FROM_CHATBOTS_AND_APPS: t('components.systemstatusmessage.unassigningContentsProgress'),
      };
      return mapping[status] || t('components.systemstatusmessage.preparingContent');
    }
  };

  // Centralized error acknowledgement logic
  const handleAcknowledgeError = async () => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(`${apiUrl}/api/v1/workspace/update-state`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          state: 'OK'
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to acknowledge error');
      }
      if (onStatusUpdate) {
        onStatusUpdate('OK');
      }
    } catch (err) {
      setInternalError(err.message || t('components.systemstatusmessage.errorAcknowledgeFailed'));
    }
  };

  // When status is OK, do not render any message.
  if (status === 'OK' || systemStatus == null) {
    return null;
  }

  // If the system status indicates an error, show an error alert with acknowledge button.
  if (status.includes('ERROR')) {
    // Use warning style for content scraping issues (less severe)
    const isContentScrapingIssue = status === 'ERROR_NO_CONTENT_SCRAPPED_FROM_WEB' ||
      status === 'ERROR_NO_CONTENT_SCRAPPED_FROM_FILE' ||
      status === 'ERROR_NO_CONTENT_SCRAPPED_FROM_GOOGLE_DOCS' ||
      status === 'ERROR_NO_CONTENT_SCRAPPED_FROM_NOTION' ||
      status === 'ERROR_NO_CONTENT_SCRAPPED_FROM_TEXT';

    return (
      <MessageContainer className={className}>
        <AlertBox className={isContentScrapingIssue ? "warning" : "error"}>
          <MessageText>
            {getErrorMessage(status)}
          </MessageText>
          <StyledButton
            variant={isContentScrapingIssue ? "warning" : "error"}
            onClick={handleAcknowledgeError}
          >
            {t('components.systemstatusmessage.acknowledge')}
          </StyledButton>
        </AlertBox>
      </MessageContainer>
    );
  }

  // Show loading message if data is still being loaded
  if (loading) {
    return (
      <MessageContainer className={className}>
        <LoadingContainer>
          <MessageText>
            {t('components.systemstatusmessage.loadingTrainedData')}
          </MessageText>
        </LoadingContainer>
      </MessageContainer>
    );
  }

  // Specific states can be rendered with their own messages
  if (status === 'TRAIN') {
    return (
      <MessageContainer className={className}>
        <AlertBox className="training">
          <MessageText>
            {t('components.systemstatusmessage.trainingInProgress')}
          </MessageText>
        </AlertBox>
      </MessageContainer>
    );
  }

  if (status === 'DELETE_CONTENT') {
    return (
      <MessageContainer className={className}>
        <AlertBox className="info">
          <MessageText>
            {t('components.systemstatusmessage.deletingContent')}
          </MessageText>
        </AlertBox>
      </MessageContainer>
    );
  }

  if (status === 'SCHEDULE_TRAINING' || status === 'SCHEDULE_TRAIN') {
    return (
      <MessageContainer className={className}>
        <AlertBox className="info">
          <MessageText>
            {t('components.systemstatusmessage.schedulingTraining')}
          </MessageText>
        </AlertBox>
      </MessageContainer>
    );
  }

  if (status === 'ASSIGN_CONTENTS_TO_CHATBOTS_AND_APPS') {
    return (
      <MessageContainer className={className}>
        <AlertBox className="info">
          <MessageText>
            {t('components.systemstatusmessage.assigningContents')}
          </MessageText>
        </AlertBox>
      </MessageContainer>
    );
  }

  if (status === 'UNASSIGN_CONTENTS_FROM_CHATBOTS_AND_APPS') {
    return (
      <MessageContainer className={className}>
        <AlertBox className="info">
          <MessageText>
            {t('components.systemstatusmessage.unassigningContents')}
          </MessageText>
        </AlertBox>
      </MessageContainer>
    );
  }

  // Otherwise, render the "prepare" message (including queue status)
  return (
    <MessageContainer className={className}>
      <AlertBox className="info">
        <MessageText>
          {getPrepareMessage()}
          {internalError && (
            <ErrorText>
              {internalError}
            </ErrorText>
          )}
        </MessageText>
      </AlertBox>
    </MessageContainer>
  );
};

export default SystemStatusMessage;
